import { ReactElement, ReactNode, useEffect, useRef } from 'react';

import { LockedRegionContainer } from './page-layout.styled';
import { usePageLocked } from './page-lock.context';

/**
 * Closes one part of a page while the rest stays usable.
 *
 * For a page whose lock does not cover everything on it: an ASC period lock freezes that period's
 * data, but a company-level control sitting next to it has no reason to be frozen, and locking it
 * out can leave a customer with no route to it at all. `inert` is inherited and cannot be undone on
 * a descendant, so the page has to mark the locked parts rather than lock everything and carve an
 * exception — which is what {@link PageLayout}'s `lockScope="regions"` hands over to the caller.
 *
 * Applies the same treatment a locked page gets, for the same reason: `pointer-events: none` alone
 * turns a region away from a mouse and nothing else — its controls keep their place in the tab
 * order, and any dialog they open is a portal rendered outside this subtree, so it works normally
 * once open.
 */
export const LockedRegion = ({ children }: { children: ReactNode }): ReactElement => {
  const regionRef = useRef<HTMLDivElement>(null);
  const locked = usePageLocked();

  // Set through a ref because React 18 does not recognise `inert` as a DOM prop.
  useEffect((): void => {
    regionRef.current?.toggleAttribute('inert', locked);
  }, [locked]);

  return (
    <LockedRegionContainer ref={regionRef} $locked={locked} direction="column" gap={6} fullWidth>
      {children}
    </LockedRegionContainer>
  );
};
